import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { AlertCircle, CheckCircle2, DollarSign, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SETTLEMENT_METHODS = [
  { value: 'Attorney/Legal', label: 'Settled via Attorney/Legal' },
  { value: 'In-House', label: 'Settled In-House' },
  { value: 'Broker/ISO', label: 'Settled via Broker/ISO' },
  { value: 'Other', label: 'Other' },
];

/**
 * Modal that captures settlement details when a deal is moving to
 * "Paid in Full / Settled".
 *
 * Props:
 *   open, onOpenChange       — control props
 *   dealId                   — deal UUID or numeric deal_id
 *   onSettled(result)        — called with the settle response on success
 */
const SettlementModal = ({ open, onOpenChange, dealId, onSettled }) => {
  const { getAuthHeaders } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementMethod, setSettlementMethod] = useState('');
  const [wentToDefault, setWentToDefault] = useState(false);
  const [legalFees, setLegalFees] = useState('');
  const [notes, setNotes] = useState('');
  const [legalCosts, setLegalCosts] = useState([]); // [{description, amount, payee_contact_id}]
  const [lawyerContacts, setLawyerContacts] = useState([]);

  useEffect(() => {
    if (!open || !dealId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/deals/${dealId}/settle-preview`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) {
          setPreview(res.data);
          // Default settlement amount = effective balance (no write-off)
          setSettlementAmount(String(res.data.effective_balance || 0));
        }
      } catch (e) {
        toast.error('Could not load deal balance');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dealId, getAuthHeaders]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setSettlementAmount('');
      setSettlementMethod('');
      setWentToDefault(false);
      setLegalFees('');
      setNotes('');
      setLegalCosts([]);
    }
  }, [open]);

  // Load lawyer contacts for the legal-cost payee picker
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/contacts?contact_type=lawyer`, {
          headers: getAuthHeaders(),
        });
        setLawyerContacts(Array.isArray(res.data) ? res.data : []);
      } catch {
        setLawyerContacts([]);
      }
    })();
  }, [open, getAuthHeaders]);

  const writtenOff = useMemo(() => {
    if (!preview) return 0;
    const amt = parseFloat(settlementAmount) || 0;
    return Math.max(preview.effective_balance - amt, 0);
  }, [preview, settlementAmount]);

  const isAttorney = settlementMethod === 'Attorney/Legal';
  const canSubmit =
    !!preview &&
    !!settlementMethod &&
    settlementAmount !== '' &&
    parseFloat(settlementAmount) >= 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const cleanedLegalCosts = (legalCosts || [])
        .filter((lc) => parseFloat(lc.amount) > 0 && (lc.description || '').trim())
        .map((lc) => ({
          description: lc.description.trim(),
          amount: parseFloat(lc.amount),
          payee_contact_id: lc.payee_contact_id || null,
        }));
      const res = await axios.post(
        `${API}/deals/${dealId}/settle`,
        {
          // The modal is only opened when the user picked "Paid in Full /
          // Settled" from the status dropdown — explicitly pass that intent so
          // the backend's inference doesn't silently downgrade to "PAID_IN_FULL"
          // when the operator entered settlement_amount equal to the remaining
          // balance (write_off would be $0 and inference would pick PIF).
          closure_type: 'SETTLED',
          settlement_amount: parseFloat(settlementAmount),
          settlement_method: settlementMethod,
          went_to_default: wentToDefault,
          lawyer_attached: isAttorney,
          lawyer_fee_amount: isAttorney && legalFees !== '' ? parseFloat(legalFees) : null,
          legal_fees: isAttorney && legalFees !== '' ? parseFloat(legalFees) : null,
          additional_legal_costs: cleanedLegalCosts.length ? cleanedLegalCosts : null,
          notes: notes || null,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(
        `Settled for ${formatCurrency(res.data.settlement_amount)} — ${formatCurrency(res.data.settlement_written_off)} written off`,
      );
      onSettled?.(res.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : detail || e.message || 'Settlement failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="settlement-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal-600" />
            Settle Deal
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">Loading deal balance…</div>
        ) : !preview ? (
          <div className="py-12 text-center text-sm text-red-500">Could not load deal info</div>
        ) : (
          <div className="space-y-4">
            {/* Balances summary */}
            <div className="rounded-lg border bg-gray-50 p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Deal</span>
                <span className="font-medium">
                  #{preview.deal_id_number} · {preview.merchant_name || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Balance Due</span>
                <span className="font-mono font-semibold">{formatCurrency(preview.balance_due)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Effective Balance (incl. fees)</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(preview.effective_balance)}
                </span>
              </div>
            </div>

            {/* Settlement Amount */}
            <div>
              <Label htmlFor="settle-amount">
                Settlement Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id="settle-amount"
                  data-testid="settlement-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-7"
                  value={settlementAmount}
                  onChange={(e) => setSettlementAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  Total amount collected to close the deal. Use the full balance if there was no write-off (e.g. full recovery via legal).
                </p>
                <button
                  type="button"
                  data-testid="settlement-full-recovery-btn"
                  onClick={() => setSettlementAmount(String(preview?.effective_balance || 0))}
                  className="text-xs font-medium text-teal-700 hover:text-teal-900 underline decoration-dotted whitespace-nowrap"
                >
                  Full recovery (no write-off)
                </button>
              </div>
            </div>

            {/* Written Off (computed) */}
            <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
              <span className="text-sm font-medium text-amber-900">Amount Written Off</span>
              <span
                className="font-mono font-bold text-amber-900"
                data-testid="settlement-written-off"
              >
                {formatCurrency(writtenOff)}
              </span>
            </div>

            {/* Settlement Method */}
            <div>
              <Label>
                How was this settled? <span className="text-red-500">*</span>
              </Label>
              <Select value={settlementMethod} onValueChange={setSettlementMethod}>
                <SelectTrigger data-testid="settlement-method-select">
                  <SelectValue placeholder="Select method..." />
                </SelectTrigger>
                <SelectContent>
                  {SETTLEMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Legal Fees - conditional on Attorney/Legal */}
            {isAttorney && (
              <div>
                <Label htmlFor="legal-fees">Legal Fees Paid</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    id="legal-fees"
                    data-testid="settlement-legal-fees-input"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    value={legalFees}
                    onChange={(e) => setLegalFees(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Amount paid to the attorney as part of this settlement.
                </p>
              </div>
            )}

            {/* Went to Default */}
            <div className="flex items-center justify-between rounded-md border bg-gray-50 px-3 py-2">
              <div>
                <div className="text-sm font-medium">Went through formal default?</div>
                <p className="text-xs text-gray-500">
                  Did this deal hit Default status before settlement?
                </p>
              </div>
              <Switch
                checked={wentToDefault}
                onCheckedChange={setWentToDefault}
                data-testid="settlement-went-to-default-toggle"
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="settle-notes">Notes (optional)</Label>
              <Textarea
                id="settle-notes"
                data-testid="settlement-notes-input"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context…"
              />
            </div>

            {/* Additional Legal Costs Editor */}
            <div className="rounded-md border border-purple-200 bg-purple-50/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-purple-900">
                  Additional Legal Costs (optional)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    setLegalCosts([
                      ...legalCosts,
                      { description: '', amount: '', payee_contact_id: '' },
                    ])
                  }
                  data-testid="legal-cost-add-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {legalCosts.length === 0 ? (
                <p className="text-xs text-purple-700/70 italic">
                  Filing fees, court costs, process server, etc. (PFS-absorbed; does not affect merchant balance.)
                </p>
              ) : (
                <div className="space-y-2">
                  {legalCosts.map((lc, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 items-end"
                      data-testid={`legal-cost-row-${idx}`}
                    >
                      <div className="col-span-5">
                        <Input
                          placeholder="Description"
                          value={lc.description}
                          onChange={(e) => {
                            const next = [...legalCosts];
                            next[idx] = { ...next[idx], description: e.target.value };
                            setLegalCosts(next);
                          }}
                          data-testid={`legal-cost-description-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Amount"
                          value={lc.amount}
                          onChange={(e) => {
                            const next = [...legalCosts];
                            next[idx] = { ...next[idx], amount: e.target.value };
                            setLegalCosts(next);
                          }}
                          data-testid={`legal-cost-amount-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Select
                          value={lc.payee_contact_id || '__none__'}
                          onValueChange={(v) => {
                            const next = [...legalCosts];
                            next[idx] = {
                              ...next[idx],
                              payee_contact_id: v === '__none__' ? '' : v,
                            };
                            setLegalCosts(next);
                          }}
                        >
                          <SelectTrigger
                            className="text-xs"
                            data-testid={`legal-cost-payee-${idx}`}
                          >
                            <SelectValue placeholder="Payee…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {lawyerContacts.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.company_name ||
                                  `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
                                  c.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
                          onClick={() =>
                            setLegalCosts(legalCosts.filter((_, i) => i !== idx))
                          }
                          data-testid={`legal-cost-remove-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {writtenOff > 0 && (
              <div className="flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  A <strong>DISCOUNT transaction for {formatCurrency(writtenOff)}</strong> will be
                  created so the ledger zeros out, and a settlement note will be added to the deal.
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="settlement-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="settlement-confirm-btn"
          >
            {submitting ? 'Settling…' : 'Confirm & Settle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettlementModal;
